import { useCallback, useEffect, useState } from "react";
import { GetBusinessCreatedCouponsAPI } from "@/services/api/coupon/getBusinessCreatedCoupons";
import type { BusinessCoupon } from "@/utils/businessCoupon";

const PAGE_SIZE = 10;

/**
 * Paginated list of the coupons created by the logged-in business user.
 * `GetBusinessCreatedCouponsAPI` returns 10 rows per page without a total
 * count, so a full page is treated as "there may be more".
 */
export function useBusinessCoupons() {
  const [coupons, setCoupons] = useState<BusinessCoupon[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchCoupons = useCallback(
    async (targetPage: number, mode: "load" | "refresh" | "more") => {
      if (mode === "refresh") setRefreshing(true);
      if (mode === "more") setLoadingMore(true);
      if (mode === "load") setLoading(true);

      const resp = await GetBusinessCreatedCouponsAPI({ page: targetPage });
      const result = Array.isArray(resp?.result)
        ? (resp.result as BusinessCoupon[])
        : [];

      setCoupons((prev) => (mode === "more" ? [...prev, ...result] : result));
      setPage(targetPage);
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    },
    [],
  );

  useEffect(() => {
    fetchCoupons(1, "load");
  }, [fetchCoupons]);

  const hasMore = coupons.length > 0 && coupons.length % PAGE_SIZE === 0;

  const refresh = useCallback(async () => {
    await fetchCoupons(1, "refresh");
  }, [fetchCoupons]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || refreshing) return;
    await fetchCoupons(page + 1, "more");
  }, [fetchCoupons, loadingMore, loading, page, refreshing]);

  return {
    coupons,
    loading,
    refreshing,
    loadingMore,
    hasMore,
    refresh,
    loadMore,
    reload: () => fetchCoupons(1, "load"),
  };
}
